<template>
  <!-- ==============================================================
       ImportantInfoWidget — always-present "Important Information"
       affordance for authenticated portal pages.

       Unlike CartWidget / TopPartsWidget (which expand into inline
       right-edge panels), this widget is ALWAYS a collapsed side-edge
       pill. Clicking it opens a MODAL that displays the CMS notices
       (System_Message_1 + DASHBOARD_FULL_WIDTH ContentBlocks) — so the
       information never occupies the right-rail stack or reserves page
       content room; it overlays on demand and dismisses cleanly.

       The pill is position:fixed (anchored mid-right, a step below
       where the cart + Top 5 pills sit when those are collapsed), so it
       sits independently of the floating widget stack. There is no
       persisted expand/collapse state: the pill always shows, fresh
       every login, exactly as requested.
       ============================================================== -->
  <div class="imp-info-widget">

    <!-- ===== ALWAYS-ON PILL =====
         Side-edge pull tab. Click (or Enter/Space) opens the modal. -->
    <portal to="important-info-pill">
    <div class="important-info-pill"
         @click="openModal"
         role="button"
         tabindex="0"
         @keydown.enter.space.prevent="openModal"
         aria-label="Open Important Information"
         title="Open Important Information">
      <div class="important-info-pill-icon">
        <!-- Alert-triangle — mirrors the ⚠ glyph in the modal title. -->
        <svg xmlns="http://www.w3.org/2000/svg"
             viewBox="0 0 24 24"
             width="22" height="22"
             fill="none"
             stroke="currentColor"
             stroke-width="2"
             stroke-linecap="round"
             stroke-linejoin="round"
             aria-hidden="true">
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
          <line x1="12" y1="9" x2="12" y2="13"/>
          <line x1="12" y1="17" x2="12.01" y2="17"/>
        </svg>
      </div>
      <div class="important-info-pill-label">INFO</div>
    </div>
    </portal>

    <!-- ===== INFORMATION MODAL =====
         Opened from the pill. Displays both CMS notices fully (no
         per-notice collapse — the modal is dedicated reading space).
         modal-class namespaces the UNSCOPED styles below (b-modal
         renders to a body portal, which scoped CSS can't reach). -->
    <b-modal id="importantInfoModal"
             v-model="showModal"
             ok-only
             ok-title="Close"
             ok-variant="outline-dark"
             size="md"
             centered
             scrollable
             modal-class="important-info-modal">
      <template #modal-title>
        <span class="important-info-modal-title">
          <span class="important-info-icon" aria-hidden="true">⚠</span>
          Important Information
        </span>
      </template>

      <div class="important-info-modal-body">
        <div class="important-info-list">
          <!-- Notice 1 — the System_Message_1 ContentBlock carries its
               own bolded heading, so no separate static headline here
               (matching the dashboard's expanded-state rendering). -->
          <div class="important-info-item">
            <div class="important-info-body">
              <ContentBlock :name="'System_Message_1'"></ContentBlock>
            </div>
          </div>

          <!-- Notice 2 — banking. Static headline + the CMS body. -->
          <div class="important-info-item">
            <div class="important-info-body">
              <div class="important-info-headline">
                J.C. Steele &amp; Sons has updated its banking information.
              </div>
              <div class="important-info-expanded">
                <ContentBlock :name="'DASHBOARD_FULL_WIDTH'"></ContentBlock>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

  </div>
</template>

<script>
export default {
  name: "ImportantInfoWidget",

  data() {
    return {
      // Drives the b-modal. Always starts closed — the pill is the only
      // resting state, so there is nothing to persist.
      showModal: false,
    };
  },

  methods: {
    openModal() {
      this.showModal = true;
    },
  },
};
</script>

<style>
/* ============================================================
   COLLAPSED PILL — UNSCOPED.
   The pill is portaled into App.vue's .floating-pill-rail (outside this
   component's DOM), so scoped CSS can't reach it — it would render
   transparent/unstyled. Namespaced (.important-info-pill*) so these
   global rules are safe. Positioning is owned by the rail; the pill is a
   static flex child. Mirrors CartWidget's unscoped .cart-pill rules.
   ============================================================ */
.important-info-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: #fff;
  color: #c0392b;
  border: 1px solid #ececec;
  border-left: 3px solid #c0392b;
  border-radius: 8px 0 0 8px;
  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 10px 8px;
  min-width: 56px;
  width: 56px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, transform 0.18s ease;
}
.important-info-pill:hover {
  background: #fafafa;
  transform: translateX(-2px);
}
.important-info-pill:focus-visible {
  outline: 2px solid #c0392b;
  outline-offset: 2px;
}
.important-info-pill-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}
.important-info-pill-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #c0392b;
  line-height: 1;
  white-space: nowrap;
}

/* ============================================================
   INFORMATION MODAL — UNSCOPED.

   b-modal renders to a body portal, so scoped CSS wouldn't reach it.
   These rules are namespaced under .important-info-modal (the modal's
   modal-class) so they're safe globally. Adapted from the
   .important-info-* rules that styled the old inline dashboard panel.
   ============================================================ */
.important-info-modal .modal-dialog {
  max-width: 520px;
}

/* Branded title: red ⚠ chip + "Important Information". */
.important-info-modal-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2a2a2a;
}
.important-info-modal .important-info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #c0392b;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  padding-bottom: 1px;
}

.important-info-modal-body {
  padding: 0.1rem 0.15rem;
}

.important-info-modal .important-info-list {
  display: flex;
  flex-direction: column;
}

/* Each notice — a block separated from the next by a thin divider. */
.important-info-modal .important-info-item {
  padding: 14px 0;
  border-top: 1px solid #f0f0f0;
}
.important-info-modal .important-info-item:first-child {
  border-top: none;
  padding-top: 2px;
}
.important-info-modal .important-info-item:last-child {
  padding-bottom: 2px;
}

.important-info-modal .important-info-body {
  font-size: 14px;
  line-height: 1.55;
  color: #333;
}

.important-info-modal .important-info-headline {
  font-size: 15px;
  font-weight: 700;
  color: #2a2a2a;
  margin-bottom: 6px;
}

.important-info-modal .important-info-expanded {
  margin-top: 2px;
}

/* Tame ContentBlock's internal markup so each notice reads
   consistently regardless of how the CMS structured it. */
.important-info-modal .important-info-body h1,
.important-info-modal .important-info-body h2,
.important-info-modal .important-info-body h3,
.important-info-modal .important-info-body h4 {
  font-size: 15px;
  font-weight: 700;
  color: #2a2a2a;
  margin: 0 0 6px 0;
  text-transform: none;
  letter-spacing: 0;
}
.important-info-modal .important-info-body p {
  margin: 0 0 8px 0;
  font-size: 14px;
  line-height: 1.55;
  color: #444;
}
.important-info-modal .important-info-body p:last-child {
  margin-bottom: 0;
}
.important-info-modal .important-info-body a {
  color: #c0392b;
  text-decoration: none;
  font-weight: 600;
}
.important-info-modal .important-info-body a:hover {
  text-decoration: underline;
}
.important-info-modal .important-info-body strong,
.important-info-modal .important-info-body b {
  font-weight: 700;
  color: #2a2a2a;
}
</style>
